import React from 'react';
import { Box, Typography, IconButton, Popover } from '@mui/material';
import { CloseIcon } from 'icons/materialIcons';
import PhotoLibraryOutlinedIcon from '@mui/icons-material/PhotoLibraryOutlined';
import DarkButtonWithIcon from 'components/common/button/DarkButtonWithIcon';

const TeamAssetPickerPopover = ({
    anchorEl,
    onClose,
    onUploadClick,
    assets = [],
    onSelectAsset,
    emptyText = 'No Team Assets Available',
    emptyActionText = 'Add team assets here',
    onEmptyActionClick,
    uploadButtonText = 'Upload your own cover photo',
    showUploadButton = true,
    showDivider = true,
    showCloseButton = true,
    footerActionText,
    onFooterAction,
    selectedAssetUrl,
    previewWidth = '180px',
    previewHeight = '70px',
    previewRadius = '8px',
    paperSx = {}
}) => {
    const open = Boolean(anchorEl);

    const getAssetDisplayName = (asset) => {
        if (asset?.name) {
            return asset.name;
        }
        if (asset?.metadata?.originalFileName) {
            return asset.metadata.originalFileName;
        }
        try {
            const urlPart = asset?.url?.split('/o/')[1];
            if (!urlPart) return 'image';
            const decodedPath = decodeURIComponent(urlPart.split('?')[0]);
            const fileName = decodedPath.split('/').pop();
            return fileName || 'image';
        } catch (error) {
            return 'image';
        }
    };

    const getAssetSavedDate = (asset) => {
        if (!asset?.createdAt) return '';
        try {
            return new Date(asset.createdAt).toLocaleDateString('en-US', {
                month: '2-digit',
                day: '2-digit',
                year: 'numeric'
            });
        } catch (error) {
            return '';
        }
    };

    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            transformOrigin={{ vertical: 'top', horizontal: 'center' }}
            PaperProps={{
                sx: {
                    borderRadius: '12px',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                    padding: '0.9rem 1rem 1rem',
                    width: '420px',
                    maxWidth: '80vw',
                    marginTop: '-0.5rem',
                    ...paperSx
                }
            }}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.15 }}>
                {showCloseButton && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '-0.45rem' }}>
                        <IconButton
                            onClick={onClose}
                            sx={{ color: '#828282' }}
                        >
                            <CloseIcon sx={{ fontSize: '1.2rem' }} />
                        </IconButton>
                    </Box>
                )}

                {showUploadButton && (
                    <DarkButtonWithIcon
                        text={uploadButtonText}
                        onClick={onUploadClick}
                        sx={{ alignSelf: 'center', marginBottom: '0.8rem' }}
                    />
                )}

                {showDivider && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, marginBottom: '0.5rem' }}>
                        <Box sx={{ flex: 1, height: '1px', backgroundColor: '#e0e0e0' }} />
                        <Typography sx={{ fontSize: '0.75rem', color: '#828282', fontWeight: 500 }}>
                            Or use a Team Asset below
                        </Typography>
                        <Box sx={{ flex: 1, height: '1px', backgroundColor: '#e0e0e0' }} />
                    </Box>
                )}

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {assets.length === 0 ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem', paddingTop: '0.35rem', paddingBottom: '0.6rem' }}>
                            <PhotoLibraryOutlinedIcon sx={{ color: '#bdbdbd', fontSize: '1.5rem', marginTop: '0.15rem', marginBottom: '0.15rem' }} />
                            <Typography sx={{ fontSize: '0.8rem', color: '#828282', textAlign: 'center' }}>
                                {emptyText}
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: '0.8rem',
                                    fontWeight: 600,
                                    color: '#79bff7',
                                    textAlign: 'center',
                                    cursor: onEmptyActionClick ? 'pointer' : 'default'
                                }}
                                onClick={onEmptyActionClick}
                            >
                                {emptyActionText}
                            </Typography>
                        </Box>
                    ) : (
                        assets.map((asset) => (
                            <Box
                                key={asset._id}
                                onClick={() => onSelectAsset?.(asset)}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 2,
                                    padding: '0.75rem',
                                    borderRadius: '10px',
                                    cursor: 'pointer',
                                    border: asset.url === selectedAssetUrl ? '2px solid #2fabed' : '1px solid #e0e0e0',
                                    '&:hover': {
                                        backgroundColor: '#f7f7f7'
                                    }
                                }}
                            >
                                <Box
                                    sx={{
                                        width: previewWidth,
                                        height: previewHeight,
                                        borderRadius: previewRadius,
                                        backgroundImage: `url(${asset.url})`,
                                        backgroundSize: 'cover',
                                        backgroundPosition: 'center',
                                        flexShrink: 0
                                    }}
                                />
                                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#4f4f4f' }}>
                                        {getAssetDisplayName(asset)}
                                    </Typography>
                                    {getAssetSavedDate(asset) && (
                                        <Typography sx={{ fontSize: '0.75rem', color: '#828282', marginTop: '0.15rem' }}>
                                            Saved on {getAssetSavedDate(asset)}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        ))
                    )}
                </Box>
                {footerActionText && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: '0.75rem' }}>
                        <DarkButtonWithIcon
                            text={footerActionText}
                            onClick={onFooterAction}
                        />
                    </Box>
                )}
            </Box>
        </Popover>
    );
};

export default TeamAssetPickerPopover;
